'use client';

import { createContext, useContext } from 'react';

/**
 * The PDP's single purchase/download action, shared across the two CTAs that
 * both trigger it: the inline digital-summary "Download Now for $X" button
 * (Row 6, in the scroll body) and the sticky-footer primary CTA. Mirrors RN,
 * where `DigitalProductSummaryRow`'s `onDownload` and the primary button both
 * call the one `onPurchase` handler.
 *
 * Living in context lets a single {@link AccessGuard} + subscribe/app-alert
 * modal stack (owned by the provider that wraps the whole `ProductDetail`) gate
 * BOTH buttons — mounting the gate twice (once per button) would double every
 * modal.
 */
export type PdpPurchaseContextValue = {
  /** Trigger the purchase/download flow: gating → checkout / external / in-app alert. */
  onPurchase: () => void;
  /**
   * `false` while auth or gate readiness is still resolving. Firebase auth
   * loads lazily, so a tap made during that window would be misrouted —
   * consumers disable their trigger control until this is `true`.
   */
  isReady: boolean;
  /**
   * The signed-in viewer already owns this digital listing, so `onPurchase`
   * opens their download instead of a checkout.
   */
  isPurchased: boolean;
};

export const PdpPurchaseContext = createContext<PdpPurchaseContextValue | null>(
  null,
);

/**
 * Read the shared PDP purchase action. Returns `null` outside the provider —
 * e.g. the digital-product editor's read-only landing preview, which composes
 * the PDP rows directly (no checkout) and keeps its CTAs inert.
 */
export function usePdpPurchase(): PdpPurchaseContextValue | null {
  return useContext(PdpPurchaseContext);
}
